import { useRef } from "react";
import { cn } from "@/lib/utils";
import { handleOfferArrowNav, isOvertimeHotkey, unitFromHotkey } from "./offer-line-nav";

const UNITS = ["t", "d", "stk", "fast"] as const;
type Unit = (typeof UNITS)[number];

interface Props {
  value: string;
  /** Hvilke enheter er valgbare. Default: alle fire. */
  allowedUnits?: readonly Unit[];
  disabled?: boolean;
  /** Kolonne for ↑/↓ mellom linjer. Tom = ikke med i grid-navigasjon (overtid). */
  navCol?: string;
  onChange: (next: Unit) => void;
  /** `o` åpner overtid på hovedlinjen (samme mønster som t/d/s/f). */
  onOvertimeHotkey?: () => void;
}

/**
 * Enhetsvelger: segmentert, klikk t/d/stk/fast direkte.
 */
export function OfferUnitCell({
  value,
  allowedUnits = UNITS,
  disabled,
  navCol,
  onChange,
  onOvertimeHotkey,
}: Props) {
  const groupRef = useRef<HTMLDivElement>(null);

  function select(next: Unit) {
    if (disabled || next === value) return;
    onChange(next);
  }

  function onKey(e: React.KeyboardEvent) {
    if (handleOfferArrowNav(e)) return;
    const next = unitFromHotkey(e.key, allowedUnits);
    if (next) {
      e.preventDefault();
      select(next);
      return;
    }
    if (isOvertimeHotkey(e.key) && onOvertimeHotkey) {
      e.preventDefault();
      onOvertimeHotkey();
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      const i = Math.max(0, allowedUnits.indexOf(value as Unit));
      select(allowedUnits[(i + 1) % allowedUnits.length]);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const i = Math.max(0, allowedUnits.indexOf(value as Unit));
      select(allowedUnits[(i - 1 + allowedUnits.length) % allowedUnits.length]);
    }
  }

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label="Enhet"
      data-offer-nav={navCol}
      onKeyDown={disabled ? undefined : onKey}
      className={cn(
        "flex h-6 w-full rounded-sm border p-px gap-px",
        "border-border/50 bg-muted/20",
        disabled && "opacity-50 pointer-events-none",
      )}
    >
      {allowedUnits.map((unit) => {
        const selected = unit === value;
        return (
          <button
            key={unit}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={unit}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            onClick={() => select(unit)}
            title={`Enhet ${unit}`}
            className={cn(
              "flex-1 min-w-0 px-0.5 text-[10px] font-medium rounded-[3px] leading-none",
              "transition-colors",
              selected
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground/80 hover:text-foreground",
            )}
          >
            {unit}
          </button>
        );
      })}
    </div>
  );
}
